import { Injectable } from '@angular/core';
import { AppConfigService } from '@markant-platform/shared/data-access';
import { UsageAggregationFilter, UsageAggregationPage } from './model/usage.model';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BillingPeriod } from '@markant-platform/shared/backoffice/model';
import { addMonth, isBetween } from '@markant-platform/shared/util-helpers';

@Injectable({
  providedIn: 'root',
})
export class UsageAggregationService {
  private today = new Date();
  private static BILLING_PERIOD_IN_MONTH: Map<BillingPeriod, number> = new Map([
    ['MONTHLY', 1],
    ['YEARLY', 12],
  ]);

  private apiUrl: string = AppConfigService.appConfiguration.apiUrl['usageServiceAggregation'];

  constructor(private readonly httpClient: HttpClient) {}

  /**
   * Fetches aggregated usages based on a given frequency to be accumulated on.<br>
   * Dates will be converted to ISO strings for the query.
   *
   * @param organizationId Identifier of an organization.
   * @param cartridgeId Identifier for a cartridge id.
   * @param serviceIds Set of service ids related to the requested service package.
   * @param frequency Frequency to aggregate usages on.
   * @param periodStart Begin of date range to get usages from.
   * @param periodEnd End of date range to get usages to.
   * @param index Page index to get the usages for.
   * @param size Number of items receive per page.
   */
  getUsagesForServicePackageAggregated(
    organizationId: string,
    cartridgeId: string,
    { serviceIds, frequency, periodStart, periodEnd }: UsageAggregationFilter,
    index = -1,
    size = -1
  ): Observable<UsageAggregationPage> {
    const query = [
      `serviceIds=${serviceIds.join('&serviceIds=')}`,
      `frequency=${frequency}`,
      `periodStart=${periodStart.toISOString()}`,
      `periodEnd=${periodEnd.toISOString()}`,
    ].join('&');

    return this.httpClient.get<UsageAggregationPage>(
      `${this.apiUrl}/${organizationId}/${cartridgeId}?index=${index}&size=${size}&${query}`
    );
  }

  /**
   * Determines the current delivery period for a booking, based on the current date.</br>
   *
   * Iterates recursively over time periods to fit the matching current period,
   * where the current date is within the range depending on the period of billing.
   * @param usageStartedAt Date when the usage has started.
   * @param billingPeriod Period the service is billed.
   */
  getCurrentDeliveryPeriod(usageStartedAt: Date, billingPeriod: BillingPeriod): Date[] {
    const monthsForBillingPeriod = UsageAggregationService.BILLING_PERIOD_IN_MONTH.get(billingPeriod);

    if (!monthsForBillingPeriod) {
      throw new Error('The targeted billing period is not supported!');
    }

    const start = new Date(usageStartedAt);
    const end = addMonth(new Date(usageStartedAt), monthsForBillingPeriod);

    if (!isBetween(start, end, this.today)) {
      return this.getCurrentDeliveryPeriod(end, billingPeriod);
    }

    return [start, end];
  }
}
